import { Injectable, InjectionToken, inject } from '@angular/core';
import { SettingsState } from '@library/utils/interfaces/settings-state.interface';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { RoomsActions } from './rooms.action';
import { CreateRoom, DeleteMeasurementsPayload, GetMeasureCsvPayload, Room, RoomNameUpdate, RoomOffsetUpdatePayload, SetTrueTemperaturePayload } from './rooms.interface';
import * as RoomsSelectors from './rooms.selector';

export const ROOMS_FACADE = new InjectionToken('ROOMS_FACADE');

/**
 * Has to be extended by a specific facade for each project
 * Extending facade must have {providedIn: 'root'} in its Injectable decorator
 */
@Injectable()
export class RoomsFacade {
  protected store$ = inject<Store<SettingsState>>(Store);
  protected actions$ = inject(Actions);

  rooms$ = this.store$.pipe(select(RoomsSelectors.getRooms));
  currentRoom$ = this.store$.pipe(select(RoomsSelectors.getCurrentRoom));
  thermMeasuredTemperature$ = this.store$.pipe(select(RoomsSelectors.getCurrentTemperature));
  modules$ = this.store$.pipe(select(RoomsSelectors.getRoomModules));
  currentRoomId$ = this.store$.pipe(select(RoomsSelectors.getCurrentRoomId));
  trueTemperatureAvailable$ = this.store$.pipe(select(RoomsSelectors.getTrueTemperatureAvailable));
  hasValve$ = this.store$.pipe(select(RoomsSelectors.hasValve));
  isCurrentRoomReachable$ = this.store$.pipe(select(RoomsSelectors.isCurrentRoomReachable));
  roomGateway$ = this.store$.pipe(select(RoomsSelectors.getRoomGateway));
  currentHomeRooms$ = this.store$.pipe(select(RoomsSelectors.getcurrentHomeRooms));

  getRoomById(roomId: string): Observable<Room> {
    if (cache[roomId]) {
      return cache[roomId];
    }
    else {
      cache[roomId] = this.store$.pipe(select(RoomsSelectors.getRoomById(roomId)));
      return cache[roomId];
    }
  }

  setCurrentRoom(roomId: Room['id']): void {
    this.store$.dispatch(RoomsActions.selectRoom({ payload: roomId }));
  }

  setTrueTemperature(params: SetTrueTemperaturePayload) {
    this.store$.dispatch(RoomsActions.setTrueTemperature({ payload: params }));

    return this.actions$.pipe(
      ofType(RoomsActions.setTrueTemperatureSuccess, RoomsActions.setTrueTemperatureFailure),
      take(1),
    );
  }

  setRoomTemperatureOffset(params: RoomOffsetUpdatePayload[]) {
    this.store$.dispatch(RoomsActions.updateRoomMeasureOffset({ payload: params }));

    return this.actions$.pipe(
      ofType(RoomsActions.updateRoomMeasureOffsetSuccess),
      take(1),
    );
  }

  getMeasureCsv(params: GetMeasureCsvPayload) {
    this.store$.dispatch(RoomsActions.getMeasureCsv({ payload: params }));

    return this.actions$.pipe(
      ofType(RoomsActions.getMeasureCsvSuccess, RoomsActions.getMeasureCsvFailure),
      take(1),
    );
  }

  getRoomMeasureCsv(params: GetMeasureCsvPayload) {
    this.store$.dispatch(RoomsActions.getRoomMeasureCsv({ payload: params }));

    return this.actions$.pipe(
      ofType(RoomsActions.getRoomMeasureCsvSuccess, RoomsActions.getRoomMeasureCsvFailure),
      take(1),
    );
  }

  deleteMeasurements(params: DeleteMeasurementsPayload) {
    this.store$.dispatch(RoomsActions.deleteMeasurements({ payload: params }));

    return this.actions$.pipe(
      ofType(RoomsActions.deleteMeasurementsSuccess, RoomsActions.deleteMeasurementsFailure),
      take(1),
    );
  }

  deleteRoom(params: { home_id: string; room_id: string }) {
    this.store$.dispatch(RoomsActions.deleteRoom({ payload: params }));

    return this.actions$.pipe(
      ofType(RoomsActions.deleteRoomSuccess, RoomsActions.deleteRoomFailure),
      take(1),
    );
  }

  createNewRoom(params: CreateRoom, header?: Record<string, string>) {
    this.store$.dispatch(RoomsActions.createNewRoom({ payload: params, header }));

    return this.actions$.pipe(
      ofType(RoomsActions.createNewRoomSuccess, RoomsActions.createNewRoomFailure),
      take(1),
    );
  }

  updateRoomName(params: RoomNameUpdate, header?: Record<string, string>) {
    this.store$.dispatch(RoomsActions.updateRoomName({ payload: params, header }));

    return this.actions$.pipe(
      ofType(RoomsActions.updateRoomNameSuccess, RoomsActions.updateRoomNameFailure),
      take(1),
    );
  }
}

const cache = {};
